import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { Add, ContentCopy } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Link } from "react-router-dom";
import { useQueryClient } from "react-query";
import axios from "axios";
import { API_ENDPOINT } from "../../constants/constants";
import { getHeader } from "../../utils/util";
import useApiEp from "../hooks/useApiEp";
import ToolsLayout, { RefreshTools, ToolsState } from "../tools/ToolsLayout";
import { invalidateTools } from "../tools/toolsData";
import { BANNER_RATIO, BANNER_SIZE_LABEL } from "./bannerImage";
import FullImageModal from "../widgets/FullImageModal";

export default function ManageBanners() {
  const query = useApiEp("admin/banners");
  const client = useQueryClient();
  const [search, setSearch] = useState(""),
    [selected, setSelected] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [preview, setPreview] = useState("");
  const banners = Array.isArray(query.data) ? query.data : [];
  const filtered = banners.filter((banner) =>
    (banner.title || "").toLowerCase().includes(search.toLowerCase()),
  );
  const malformed = query.data && !Array.isArray(query.data);
  const remove = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await axios.delete(
        `${API_ENDPOINT}admin/banners/${encodeURIComponent(selected._id)}`,
        getHeader(),
      );
      await invalidateTools(client);
      setNotice("Banner deleted.");
      setSelected(null);
    } catch (err) {
      setError(err.response?.data?.message || "Unable to delete this banner. Please retry.");
    } finally {
      setBusy(false);
    }
  };
  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setNotice("Image URL copied.");
    } catch (_) {
      setNotice("Unable to copy the image URL. Open the image and copy its address.");
    }
  };
  return (
    <ToolsLayout
      title="Market banners"
      description={`Mobile slider banners · ${BANNER_SIZE_LABEL} · 2:1 aspect ratio.`}
      actions={
        <>
          <RefreshTools onClick={query.refetch} loading={query.isFetching} />
          <Button component={Link} to="/home/add-banner" variant="contained" startIcon={<Add />}>
            Add banner
          </Button>
        </>
      }
    >
      {notice && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}
      <section className="tools-panel">
        <div className="tools-panel-heading">
          <div>
            <Typography component="h2" variant="h6">
              Banner library
            </Typography>
            <p>
              {query.isLoading
                ? "Loading banners…"
                : query.isError || malformed
                  ? "Data unavailable"
                  : `${banners.length} banners in the market`}
            </p>
          </div>
          <TextField
            size="small"
            label="Search banners"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <ToolsState
          loading={query.isLoading}
          error={query.isError || malformed}
          retry={query.refetch}
          empty={!filtered.length}
          emptyTitle={search ? "No matching banners" : "Create your first banner"}
          emptyMessage={
            search
              ? "Try another title or clear your search."
              : "Upload a promotional image and a title for the market."
          }
          action={
            search ? (
              <Button onClick={() => setSearch("")}>Clear search</Button>
            ) : (
              <Button component={Link} to="/home/add-banner">
                Add banner
              </Button>
            )
          }
        >
          <Box
            sx={{
              p: 3,
              pt: 0,
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                md: "repeat(2, minmax(0, 1fr))",
              },
              gap: 3,
            }}
          >
            {filtered.map((banner) => (
              <Box
                key={banner._id}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <Box
                  component="button"
                  type="button"
                  aria-label={`Preview ${banner.title}`}
                  onClick={() => setPreview(banner.imageUrl)}
                  sx={{
                    display: "block",
                    width: "100%",
                    border: 0,
                    p: 0,
                    cursor: "zoom-in",
                    bgcolor: "background.default",
                  }}
                >
                  <img
                    src={banner.imageUrl}
                    alt={banner.title}
                    style={{
                      width: "100%",
                      aspectRatio: String(BANNER_RATIO),
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 2 }}>
                    {banner.title}
                  </Typography>
                  <div className="tools-actions">
                    <Button
                      variant="outlined"
                      size="small"
                      component={Link}
                      to={`/home/${encodeURIComponent(banner._id)}/edit-banner`}
                    >
                      Edit banner
                    </Button>
                    <Button
                      size="small"
                      startIcon={<ContentCopy fontSize="small" />}
                      onClick={() => copy(banner.imageUrl)}
                    >
                      Copy URL
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        setError("");
                        setSelected(banner);
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </Box>
              </Box>
            ))}
          </Box>
        </ToolsState>
      </section>
      <FullImageModal open={Boolean(preview)} handleClose={() => setPreview("")} imgUrl={preview} />
      <Dialog
        open={Boolean(selected)}
        onClose={busy ? undefined : () => setSelected(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete banner?</DialogTitle>
        <DialogContent>
          <Typography>“{selected?.title}” will be removed from the market.</Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setSelected(null)}>
            Cancel
          </Button>
          <LoadingButton loading={busy} variant="contained" color="error" onClick={remove}>
            Delete banner
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </ToolsLayout>
  );
}
